import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { uploadFile } from '../utils/uploadFile';
import { fileUrl } from '../utils/fileUrl';
import { fileDelete } from '../utils/fileDelete';
import DocumentScanner from './DocumentScanner';
import ErrorBoundary from '../components/ErrorBoundary';
import { cascadeMatch } from '../utils/cascadeMatch';
import XlsxPreview from '../components/XlsxPreview';
import { CLIENT_LEVEL, buildFilingUpdates } from '../constants/docFiling';
import DocTypeOptions from '../components/DocTypeOptions';
import { useDocVocab, DOC_KEYS, DOC_LEVELS } from '../utils/docVocab';
import { loadPileFiles, isSignInRefused } from '../utils/intakePile';
import { fileWithoutScanning, skipBlockers } from '../utils/fileWithoutScanning';

// FOLDERS AND TYPES COME FROM THE TABLE (doc_folders / doc_types, utils/docVocab): the Document Type list, the
// Folder Reference pills and tooltips, the "→ folder" hint, which types must be scanned (scan_as) and which file
// at the client (the folder's level). Nothing here lists a folder or type by name any more.
// This is the fork: a type with scan_as set goes to Scan (which resolves the property); any other type shows the
// Property field to file manually.
const YEARS = Array.from({ length: 12 }, (_, i) => String(2018 + i));

// ── Scan animation overlay ────────────────────────────────────────────────────
function ScanAnimation({ previewUrl, onDone }) {
  const [phase, setPhase] = useState('scanning'); // scanning | done
  const [linePos, setLinePos] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());
  const DURATION = 2200; // ms per sweep
  const SWEEPS = 3;

  useEffect(() => {
    let sweep = 0;
    function animate() {
      const elapsed = Date.now() - startRef.current;
      const pct = (elapsed % DURATION) / DURATION;
      // ping-pong
      const pos = sweep % 2 === 0 ? pct * 100 : (1 - pct) * 100;
      setLinePos(pos);
      if (elapsed >= DURATION * SWEEPS) {
        setPhase('done');
        setTimeout(onDone, 600);
        return;
      }
      if (elapsed % DURATION < 16) sweep++;
      rafRef.current = requestAnimationFrame(animate);
    }
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [onDone]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#0a0f1e', borderRadius: '0 0 12px 12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Blurred document background */}
      {previewUrl && (
        <iframe
          src={previewUrl}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', filter: 'blur(1px) brightness(0.35)', pointerEvents: 'none' }}
          title="scan-bg"
        />
      )}
      {/* Scan overlay */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
        {/* Scan line */}
        <div style={{
          position: 'absolute', left: '5%', right: '5%',
          top: `${linePos}%`,
          height: '3px',
          background: 'linear-gradient(90deg, transparent, #B40000 20%, #ffffff 50%, #B40000 80%, transparent)',
          boxShadow: '0 0 18px 6px rgba(180,0,0,0.6), 0 0 40px 12px rgba(180,0,0,0.25)',
          borderRadius: '2px',
          transition: 'top 0.016s linear',
          zIndex: 3,
        }} />
        {/* Scan area border */}
        <div style={{
          position: 'absolute', left: '5%', right: '5%', top: '2%', bottom: '2%',
          border: '1px solid rgba(180,0,0,0.2)', borderRadius: '4px',
          boxShadow: 'inset 0 0 30px rgba(180,0,0,0.05)',
        }} />
        {/* Corner brackets */}
        {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
          <div key={`${v}${h}`} style={{
            position: 'absolute',
            [v]: '2%', [h]: '5%',
            width: '20px', height: '20px',
            borderTop: v==='top' ? '2px solid #B40000' : 'none',
            borderBottom: v==='bottom' ? '2px solid #B40000' : 'none',
            borderLeft: h==='left' ? '2px solid #B40000' : 'none',
            borderRight: h==='right' ? '2px solid #B40000' : 'none',
            boxShadow: '0 0 8px rgba(180,0,0,0.5)',
          }} />
        ))}
        {/* Status */}
        <div style={{
          position: 'absolute', bottom: '8%',
          background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(180,0,0,0.3)',
          borderRadius: '20px', padding: '6px 18px', fontSize: '12px', fontWeight: '700',
          color: '#B40000', letterSpacing: '0.1em', fontFamily: "'Public Sans',system-ui,sans-serif",
        }}>
          {phase === 'done' ? '✓ EXTRACTION COMPLETE' : '⟳ SCANNING DOCUMENT...'}
        </div>
      </div>
    </div>
  );
}

export default function DocumentIntake({ darkMode, staffUser, allProperties, allClients, onDrillParcel }) {
  const [sessions, setSessions]           = useState([]);
  const [needToReview, setNeedToReview]   = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionFiles, setSessionFiles]   = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls]     = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);   // the one row whose ⬇ is in flight

  // Step-Gap1: Task-context filter support.
  // When TaskEngine's "Process in Document Intake" button tab-switches to this
  // page, it writes sidecar keys to sessionStorage (wh_intake_token and
  // wh_intake_return_task). We pick them up on mount and use them to filter
  // sessions + show a "Back to task" button.
  const [taskContext, setTaskContext] = useState(() => {
    try {
      const token = sessionStorage.getItem('wh_intake_token');
      const returnTask = sessionStorage.getItem('wh_intake_return_task');
      if (!token) return null;
      return { token, returnTask: returnTask || null };
    } catch {
      return null;
    }
  });

  // scanner — now inline, not overlay
  const [scannerMode, setScannerMode]     = useState(false);  // true = show inline scanner below preview

  // Filing-form pane width — persisted to localStorage so it sticks across sessions.
  // Range: 240–600px. Default 260px.
  const [formPaneWidth, setFormPaneWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('wh_intake_form_width') || '260', 10);
      if (Number.isFinite(v) && v >= 240 && v <= 600) return v;
    } catch {}
    return 260;
  });
  useEffect(() => {
    try { localStorage.setItem('wh_intake_form_width', String(formPaneWidth)); } catch {}
  }, [formPaneWidth]);

  // Drag handler — attaches mousemove + mouseup to window during a drag.
  // Computes new width from the parent flex container's right edge minus mouse X.
  const startDragFormPane = useCallback((e) => {
    e.preventDefault();
    const parentRect = e.currentTarget.parentElement.getBoundingClientRect();
    const parentRight = parentRect.right;
    const onMove = (ev) => {
      const desired = parentRight - ev.clientX;
      const clamped = Math.max(240, Math.min(600, desired));
      setFormPaneWidth(clamped);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);
  const [scanning, setScanning]           = useState(false);  // true = animation playing
  const [scannerFile, setScannerFile]     = useState(null);
  const [autoReturnCountdown, setAutoReturnCountdown] = useState(null); // null = inactive; otherwise seconds remaining

  const [form, setForm] = useState({
    clientId: '', clientName: '', consultantId: '', docType: '', property: null,
    year: String(new Date().getFullYear()), parcelId: '',
    viewableClient: false, viewableConsultant: false, needReview: false,
  });
  const [clientSearch, setClientSearch]   = useState('');
  const [propSearch, setPropSearch]       = useState('');
  const [clientProps, setClientProps]     = useState([]);
  const [clientParcels, setClientParcels] = useState([]);
  const [filing, setFiling]              = useState(false);
  const { vocab } = useDocVocab();   // folders & types — filing resolves the chosen type through it
  // Consultant firms, for a consultant-level type (doc_folders.level 'consultant'): filed against the firm, no client.
  const [consultants, setConsultants]    = useState([]);
  const [consultantsErr, setConsultantsErr] = useState('');
  const [filed, setFiled]                = useState(null);
  // "File without scanning" for a scan-required type (utils/fileWithoutScanning): null = off; otherwise
  // { prevYear } — the year the form held before, restored on Cancel (skip mode starts with NO year, by rule).
  const [skipMode, setSkipMode]          = useState(null);
  const [skipReason, setSkipReason]      = useState('');
  const [skipError, setSkipError]        = useState('');
  const [splitting, setSplitting]         = useState(false);
  // ⛔ Split used to fail in total silence: two console.error swallows, plus a `if (result.pages)` that
  //    skipped without a word, plus a single-page success that changed nothing on screen. It cost a week
  //    and a half of "the button does nothing" with nothing to report. Every outcome now names itself here,
  //    keyed to the row it belongs to: { id, text, tone: 'error' | 'info' }.
  const [splitMsg, setSplitMsg]           = useState(null);
  const [renaming, setRenaming]           = useState(null);   // { id, stem } — the row being renamed, and its edited STEM (never the extension)
  const [renameMsg, setRenameMsg]         = useState(null);   // { id, text } — why a rename was refused, on the row it belongs to
  const [renameSaving, setRenameSaving]   = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [filePresence, setFilePresence]     = React.useState({});
  const heartbeatRef                        = React.useRef(null);
  const activeFileRef                       = React.useRef(null);
  const [identifyingId, setIdentifyingId]   = React.useState(null); // fileId currently being pre-identified (CLIENT spinner)
  const [identifyFailedId, setIdentifyFailedId] = React.useState(null); // fileId whose identify finished without resolving a client
  const [staffUploading, setStaffUploading] = React.useState(false); // staff-upload in progress
  const [dropHot, setDropHot]               = React.useState(false); // drag-over highlight on the drop fixture
  const [staffUploadMsg, setStaffUploadMsg] = React.useState(null);  // { text, names } — files NOT saved; stays until dismissed (never fail silently)
  const [staffQueued, setStaffQueued]       = React.useState(0);     // files dropped while an upload runs, waiting their turn
  const staffBusyRef                        = React.useRef(false);   // the live "upload running" flag (state is stale inside a running upload)
  const staffQueueRef                       = React.useRef([]);      // those queued files
  const staffFileInputRef                   = React.useRef(null);

  const isDark = darkMode;
  const c = isDark ? {
    bg: 'var(--ink)', card: 'rgba(47,56,84,0.6)', border: '#374060',
    text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc',
    input: 'var(--ink)', inputBorder: '#434C68', gold: 'var(--red)',
    th: 'rgba(26,34,56,0.5)', rowHover: 'rgba(255,255,255,0.03)',
    preview: '#111827',
  } : {
    bg: 'var(--bg)', card: '#ffffff', border: 'var(--line)',
    text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068',
    input: '#ffffff', inputBorder: '#c8ccd8', gold: 'var(--red-dark)',
    th: 'var(--line-soft)', rowHover: 'rgba(0,0,0,0.02)',
    preview: '#e8eaf2',
  };

  const s = {
    page:      { fontFamily: "'Public Sans',system-ui,sans-serif", color: c.text },
    card:      { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH:     { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT:     { fontSize: '14px', fontWeight: '700', color: c.text },
    btn:       { padding: '7px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnSm:     { padding: '4px 10px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '5px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost:  { padding: '6px 14px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.sub, fontSize: '12px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    input:     { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    select:    { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" },
    label:     { fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px', display: 'block' },
    badge:     (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }),
  };

  const loadSessions = useCallback(async () => {
    const { data: tokens } = await supabase
      .from('upload_tokens')
      .select('*')
      .gt('files_received', 0)
      .order('last_upload_at', { ascending: false });
    if (!tokens) return;

    // For each token, try to find its linked document_request to get the request_code.
    // We fetch all in one query for efficiency.
    const tokenIds = tokens.map(t => t.id);
    let requestCodeByTokenId = {};
    if (tokenIds.length > 0) {
      const { data: linkedReqs } = await supabase.from('document_requests')
        .select('upload_token_id, request_code')
        .in('upload_token_id', tokenIds);
      (linkedReqs || []).forEach(r => {
        if (r.upload_token_id && r.request_code) {
          requestCodeByTokenId[r.upload_token_id] = r.request_code;
        }
      });
    }

    const sessionData = await Promise.all(tokens.map(async (tok) => {
      // A split original (split_at) is hidden: its pages are in the pile in its place.
      const unfiled = await loadPileFiles(tok.token);
      if (!unfiled || unfiled.length === 0) return null;

      // Group by upload_session so each upload batch is a separate folder
      const sessionGroups = {};
      unfiled.forEach(f => {
        const sid = f.upload_session || 'legacy';
        if (!sessionGroups[sid]) sessionGroups[sid] = [];
        sessionGroups[sid].push(f);
      });

      const requestCode = requestCodeByTokenId[tok.id] || null;

      return Object.entries(sessionGroups).map(([sid, sessionFiles]) => {
        const uploadedAt = sessionFiles[0]?.created_at || tok.created_at;
        const comment = sessionFiles.find(f => f.uploader_comment)?.uploader_comment || null;
        const uploaderType = sessionFiles[0]?.uploader_type || 'client';
        const isStaff = uploaderType === 'staff';
        const uploaderName = sessionFiles[0]?.uploaded_by_name || tok.client_name;
        const dateStr = new Date(uploadedAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
        return {
          tokenId: `${tok.id}__${sid}`,
          token: tok.token,
          requestCode,
          clientId: tok.client_id, clientName: tok.client_name,
          propertyId: tok.property_id, propertyName: tok.property_name,
          fileCount: sessionFiles.length, files: sessionFiles,
          uploadedAt, comment, uploaderType, uploaderName,
          sessionLabel: isStaff ? null : `${uploaderName} · ${dateStr}`,   // staff pile: no per-batch date (it's "my pile")
          isConsultant: uploaderType === 'consultant',
          isStaff,
        };
      });
    }));

    setSessions(sessionData.filter(Boolean).flat());
    const { data: reviewFiles } = await supabase.from('file_exchange').select('*').eq('need_review', true).eq('direction', 'client_to_ntrg').is('split_at', null);
    setNeedToReview(reviewFiles || []);
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Staff upload — drop local files into "my pile" ──────────────────────────
  // A file must attach to an upload_token to appear in Intake (loadSessions is token-driven). So staff uploads use a
  // STAFF-scoped token (one per staff member): uploader_type='staff', client_name = the staff "First L" (the card
  // label), client left NULL (the client is identified per-file at filing time — this is a pile, not a client folder).
  // A FIXED upload_session rolls all of a staff member's drops into ONE card. Visible to all staff via loadSessions.
  const staffLabel = (() => {
    const parts = String(staffUser?.name || 'Me').trim().split(/\s+/).filter(Boolean);
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}` : (parts[0] || 'Me');
  })();
  async function getStaffToken() {
    const token = `staff-${staffUser?.id || 'unknown'}`;
    const { data: existing } = await supabase.from('upload_tokens').select('*').eq('token', token).maybeSingle();
    if (existing) return existing;
    // expires_at is NOT NULL on upload_tokens; a staff pile is a PERSISTENT internal folder (not an expiring share
    // link), so set it ~10 years out to satisfy the constraint. loadSessions filters only on files_received > 0 —
    // never on expires_at — so the far date has no rendering effect. created_by is a TEXT name by convention.
    const farFuture = new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString();
    const { data: created, error } = await supabase.from('upload_tokens')
      .insert({ token, client_id: null, client_name: staffLabel, uploader_type: 'staff', created_by: staffUser?.name || 'NTRG Staff', active: true, files_received: 0, expires_at: farFuture })
      .select().single();
    if (error) { console.error('[staff-upload] token create failed:', error.message); return null; }
    return created;
  }
  // ⛔ NO DROP IS EVER IGNORED AND NO FAILURE IS A PASSING HINT (2026-11-03). A drop while an upload runs used to be
  // thrown away (`if (staffUploading) return`); it is now queued and uploaded when the current one finishes. Every
  // file that was not saved is NAMED in a list that stays until dismissed — an 8-second "N of M failed" let 19
  // rejected Hertz bills go unnoticed. A refused sign-in (401) stops the run: every later file would be refused too,
  // so they are listed as not saved rather than tried. There is deliberately no automatic session refresh + retry
  // here — the 2026-08-09 auto-recovery caused a request storm and was reverted (memory: session-auth-state-aug9).
  async function handleStaffUpload(fileList) {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    if (staffBusyRef.current) {
      staffQueueRef.current.push(...files);
      setStaffQueued(staffQueueRef.current.length);
      return;
    }
    staffBusyRef.current = true;
    setStaffUploading(true);
    setStaffUploadMsg(null);
    const all = [...files];
    const saved = new Set();
    let signedOut = false;
    let failure = null;
    try {
      const tok = await getStaffToken();
      if (!tok) throw new Error('Could not open your upload folder — token creation failed (see console).');
      const sessionId = `staff-${staffUser?.id || 'unknown'}`;   // FIXED → one card per staff (the whole pile)
      let batch = files;
      while (batch.length && !signedOut) {
        for (const file of batch) {
          if (signedOut) break;
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const storagePath = `general/root/${Date.now()}-${Math.random().toString(36).slice(2, 8)}_${safeName}`;   // unique per file
          const { error: upErr } = await uploadFile(storagePath, file);
          if (upErr) {
            console.error('[staff-upload] storage:', file.name, upErr.message);
            if (isSignInRefused(upErr)) signedOut = true;
            continue;
          }
          const { error: fxErr } = await supabase.from('file_exchange').insert({
            client_id: null, property_id: null, upload_token: tok.token,
            uploader_type: 'staff', uploaded_by_name: staffUser?.name || 'NTRG Staff',
            direction: 'client_to_ntrg', file_name: file.name, file_size_bytes: file.size,
            storage_path: storagePath, folder_key: DOC_KEYS.clientUploads, file_type: null,
            // ⛔ Staff drops are NOT auto-flagged for review — staff choose/name/categorize their own uploads.
            // The Need-to-Review queue is for PUBLIC uploads (client-submitted, unattended), flagged separately
            // at api/upload-record.js:50. A staffer who wants review uses the filing form's checkbox.
            upload_session: sessionId, viewable_client: false, viewable_consultant: false, need_review: false,
          });
          if (fxErr) { console.error('[staff-upload] file_exchange:', file.name, fxErr.message); continue; }
          saved.add(file);
        }
        batch = staffQueueRef.current.splice(0);   // drops made while this ran
        all.push(...batch);
        setStaffQueued(0);
      }
      if (saved.size > 0) {
        await supabase.from('upload_tokens').update({ files_received: (tok.files_received || 0) + saved.size, last_upload_at: new Date().toISOString() }).eq('id', tok.id);
        await loadSessions();
      }
    } catch (e) {
      console.error('[staff-upload]', e);
      failure = e?.message || 'Upload failed.';
    } finally {
      all.push(...staffQueueRef.current.splice(0));   // queued but never reached (sign-in refused, or an error)
      setStaffQueued(0);
      const notSaved = all.filter(f => !saved.has(f)).map(f => f.name);
      if (notSaved.length) {
        setStaffUploadMsg({
          text: signedOut
            ? `Your sign-in was refused, so ${notSaved.length} of ${all.length} file${all.length === 1 ? ' was' : 's were'} not saved. Reload the page (sign in again if asked), then drop these again:`
            : `${notSaved.length} of ${all.length} file${all.length === 1 ? ' was' : 's were'} not saved${failure ? ` (${failure})` : ' (see console)'}. Drop these again:`,
          names: notSaved,
        });
      }
      staffBusyRef.current = false;
      setStaffUploading(false);
    }
  }


  useEffect(() => {
    supabase.from('consultants').select('id, company').order('company').then(({ data, error }) => {
      if (error) { setConsultantsErr(error.message); return; }
      setConsultants(data || []);
    }, (e) => setConsultantsErr(e.message || 'could not load consultants'));
  }, []);

  useEffect(() => {
    if (!form.clientId) { setClientProps([]); setClientParcels([]); return; }
    const props = (allProperties || []).filter(p => p.client_id === form.clientId);
    setClientProps(props);
    setClientParcels([]);
    // Keep a property that already belongs to this client — the pre-identify sets client+property together for the
    // SAME client and would otherwise be wiped. A real client switch (property is for a different client, or none)
    // still clears downstream as before.
    setForm(f => (f.property && String(f.property.client_id) === String(f.clientId)) ? f : { ...f, property: null, parcelId: '' });
  }, [form.clientId, allProperties]);

  useEffect(() => {
    if (!form.property) { setClientParcels([]); return; }
    supabase.from('parcels').select('id, parcel_name, account_number, re_pp')
      .eq('master_property_id', form.property.master_property_id)
      .then(({ data }) => setClientParcels(data || []));
  }, [form.property]);

  // Auto-return to TaskEngine when all files for a task-context request are done.
  // Triggers when:
  //   * we arrived from a TaskEngine task (taskContext.token + returnTask set)
  //   * sessions list has no entries for this token (i.e., everything filed)
  // Counts down from 2s while showing a banner, then routes via URL.
  useEffect(() => {
    if (!taskContext?.token || !taskContext?.returnTask) return;
    const stillUnfiled = sessions.some(s => s.token === taskContext.token);
    if (stillUnfiled) {
      // Reset countdown if we re-enter unfiled state (e.g., admin re-flags as need-review)
      if (autoReturnCountdown !== null) setAutoReturnCountdown(null);
      return;
    }
    // All filed — start countdown
    if (autoReturnCountdown === null) {
      setAutoReturnCountdown(2);
      return;
    }
    if (autoReturnCountdown <= 0) {
      try { sessionStorage.setItem('wh_intake_return_task', taskContext.returnTask); } catch {}
      window.location.href = `${window.location.pathname}?page=tasks`;
      return;
    }
    const id = setTimeout(() => setAutoReturnCountdown(autoReturnCountdown - 1), 1000);
    return () => clearTimeout(id);
  }, [sessions, taskContext, autoReturnCountdown]);

  // ⛔ SIGN ON DEMAND — NEVER IN BULK. Opening a pile used to mint a 'preview' URL for EVERY file in it, and the
  //    preview pane renders exactly ONE: a 10-file pile cost 10 presigns and 10 file_access_log rows to show one
  //    file. Across Intake that was ~89k signing round-trips, the 4th-largest Disk IO consumer in the database
  //    (every mint writes an audit row), and it filled the audit trail with "previews" of files nobody opened.
  //    Sign where the user actually reaches for a file: on selection, and on the per-row split button.
  //    fileUrl caches on action+path for 300s and collapses concurrent calls for the same file, so re-selecting
  //    or re-splitting the same file is free. Returns null on failure — every caller already handles a null URL.
  async function signedUrlFor(file) {
    if (!file) return null;
    if (file.signedUrl) return file.signedUrl;
    if (!file.storage_path) return null;
    let su = null; try { su = await fileUrl(file.storage_path, 'preview'); } catch { su = null; }
    if (su) setSessionFiles(prev => prev.map(f => f.id === file.id ? { ...f, signedUrl: su } : f));
    return su;
  }
  // Selecting a file is asynchronous now (it signs), so a fast second click must win. Each call takes a ticket;
  // a call whose ticket is stale by the time its signing returns drops its results rather than overwriting the
  // newer selection — and skips the prefill/identify below, which belong to the file the user has moved off.
  const selectSeq = useRef(0);

  function openSession(session) {
    if (activeSession?.tokenId === session.tokenId) {
      setActiveSession(null); setSessionFiles([]); setSelectedFiles([]); setPreviewUrls([]);
      setScannerMode(false); setScanning(false);
      return;
    }
    setActiveSession(session);
    setSelectedFiles([]); setPreviewUrls([]);
    setScannerMode(false); setScanning(false);
    setRenaming(null); setRenameMsg(null);   // an open rename belongs to the pile being left
    setSessionFiles(session.files.map(f => ({ ...f, signedUrl: null })));
  }

  // ⛔ SPLIT MAKES REAL DOCUMENTS. It used to make browser-only rows that BORROWED the original's id, so filing,
  //    dismissing or deleting a "page" acted on the whole original — 🗑 on "… — 2 of 4.pdf" destroyed the
  //    4-page PDF. api/split-pdf now reads the original by its own key, writes one object + one file_exchange row
  //    per page (PDF) or per sheet (workbook), and hides the original (split_at). Each page is then an ordinary
  //    row: ✎ / ⬇ / ✓ / 🗑 / File / Scan act on that page and nothing else. The original is never deleted; Undo
  //    split brings it back. Server-side, so no bytes cross a function either way (the ~4.5 MB cap).
  const callSplitApi = async (action, fileExchangeId) => {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session && session.access_token;
    return fetch('/api/split-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ action, file_exchange_id: fileExchangeId }),
    });
  };
  const kb = (n) => `${Math.max(1, Math.round((n || 0) / 1024)).toLocaleString('en-US')} KB`;

  async function splitFile(file) {
    setSplitMsg(null);
    if (!file.storage_path) { setSplitMsg({ id: file.id, tone: 'error', text: 'This file has no stored copy to split.' }); return; }
    setSplitting(true);
    try {
      // ⛔ CHECK ok BEFORE PARSING — an error body is not guaranteed to be JSON (the platform's own 413 is text).
      const resp = await callSplitApi('split', file.id);
      if (!resp.ok) { setSplitMsg({ id: file.id, tone: 'error', text: await splitFailureText(resp) }); return; }
      const result = await resp.json();
      if (!result.pages?.length) {
        // A one-page PDF / one-sheet workbook is a SUCCESS that changes nothing — say so, or it looks like a failure.
        setSplitMsg({ id: file.id, tone: 'info', text: `This file has only one ${/\.pdf$/i.test(file.file_name || '') ? 'page' : 'sheet'} — nothing to split.` });
        return;
      }
      await refreshOpenPile();
      await loadSessions();
      const firstId = result.pages[0].id;
      setSplitMsg({ id: firstId, tone: 'info', text: `Split into ${result.pages.length}: ${result.page_bytes.map(kb).join(' · ')} (original ${kb(result.original_bytes)}, kept and hidden — Undo split restores it).` });
    } catch (e) {
      console.error('[splitFile]', e);
      setSplitMsg({ id: file.id, tone: 'error', text: `Could not split this file: ${e.message}` });
    } finally {
      // finally, not a trailing call: the early returns are inside the try.
      setSplitting(false);
    }
  }

  // Undo split — removes every page of this page's original (refused if any page was already filed) and brings
  // the original back into the pile.
  async function undoSplit(item, e) {
    e.stopPropagation();
    const siblings = sessionFiles.filter(f => f.split_from_id === item.split_from_id).length;
    if (!window.confirm(`Undo the split?\n\nThis removes all ${siblings} page(s) split from the original and puts the original back in the pile. Renames on the pages are lost. The original itself is untouched.`)) return;
    setSplitMsg(null);
    setSplitting(true);
    try {
      const resp = await callSplitApi('unsplit', item.id);
      if (!resp.ok) { setSplitMsg({ id: item.id, tone: 'error', text: await splitFailureText(resp) }); return; }
      setSelectedFiles([]); setPreviewUrls([]);
      await refreshOpenPile();
      await loadSessions();
    } catch (err) {
      setSplitMsg({ id: item.id, tone: 'error', text: `Could not undo the split: ${err.message}` });
    } finally {
      setSplitting(false);
    }
  }

  // After a page is deleted: if it was the LAST page of its original, the original comes back (the server only
  // restores when no page of it remains anywhere — a sibling filed or sent to review keeps it hidden).
  async function restoreIfEmpty(item) {
    if (!item.split_from_id) return;
    try {
      const resp = await callSplitApi('restore', item.id);
      const result = resp.ok ? await resp.json() : null;
      if (result?.restored) { await refreshOpenPile(); await loadSessions(); }
    } catch (e) { console.warn('[restoreIfEmpty]', e); }
  }

  // Read a failed split response without assuming JSON, and name the reason in words a triager can act on.
  async function splitFailureText(resp) {
    const raw = await resp.text().catch(() => '');
    let reason = '';
    try { reason = (JSON.parse(raw) || {}).error || ''; } catch { reason = raw.slice(0, 120); }
    if (resp.status === 413) return 'That file is too large to send to the splitter.';
    if (resp.status === 401 || resp.status === 403) return 'Your session has expired — sign in again to split files.';
    if (reason === 'object_missing') return 'The stored copy of this file is missing from storage.';
    // The endpoint answers in sentences ("already split — undo the split first", "2 page(s) already filed…"),
    // and one status code covers several of them, so its own words win over a status-code guess.
    return reason || `The splitter failed (error ${resp.status}).`;
  }

  // Split a filename into [stem, extension]. `i > 0` so a leading-dot name (".pdf") is all stem and keeps
  // its dot rather than being read as a bare extension.
  const splitName = (name) => {
    const str = String(name || '');
    const i = str.lastIndexOf('.');
    return (i > 0 && i < str.length - 1) ? [str.slice(0, i), str.slice(i)] : [str, ''];
  };

  // ⛔ RENAME EDITS THE STEM ONLY — the extension is re-appended, never typed. It is not cosmetic: the
  //    extension decides which split button appears and whether AI pre-identify runs (:495-496, :604), the
  //    row icon, and whether NoticeExtractor sees the file at all (NoticeExtractor.js:161). A user who
  //    retyped "Smith invoice" over "scan0001.pdf" would silently lose all of it. Taking the extension out
  //    of the editable text removes the hazard instead of validating against it.
  async function saveRename(item) {
    if (renameSaving) return;   // Enter is live while the write is in flight — one rename, one log entry
    const [, ext] = splitName(item.file_name);
    const stem = String((renaming && renaming.stem) || '').trim();
    if (!stem) { setRenameMsg({ id: item.id, text: 'Give the file a name.' }); return; }
    // ⛔ REFUSE '[REMOVED'. That prefix is a SOFT-DELETE MARKER, not decoration: TaskEngine.js:1909 writes
    //    it, and DocumentsBrowser.js:250 + ClientDocuments.js:51 hide every row whose name starts with it.
    //    A rename that added it would hide a live document from staff AND the client; one that cleared it
    //    would resurrect a deleted one. Neither belongs behind a triage-screen text box.
    if (/^\[removed/i.test(stem)) {
      setRenameMsg({ id: item.id, text: 'A name can\u2019t start with \u201c[REMOVED\u201d \u2014 that marks a deleted file.' });
      return;
    }
    const next = `${stem}${ext}`;
    if (next === item.file_name) { setRenaming(null); setRenameMsg(null); return; }
    setRenameSaving(true);
    try {
      // ⛔ .select('id') for the AFFECTED ROW COUNT, not just { error }. file_exchange is RLS-enabled with
      //    staff-only policies and no anon policy, so a blocked update returns 0 rows and NO error — the
      //    documented silent-success class. Same two-part check as the filing write at :832.
      const { data: updated, error } = await supabase.from('file_exchange')
        .update({ file_name: next }).eq('id', item.id).select('id');
      if (error || !updated || updated.length === 0) {
        setRenameMsg({ id: item.id, text: error ? `Could not rename: ${error.message}` : 'The rename affected 0 rows — it was blocked, not saved.' });
        return;
      }
      // ⛔ KEEP THE ORIGINAL NAME. A client-supplied filename is evidence of what they sent; after this
      //    write the only other copy is inside the R2 key, which nothing in the UI can read. Logged so the
      //    question "what did the client actually call this" has an answer a year from now.
      await supabase.from('activity_log').insert({
        staff_user: staffUser?.name || 'NTRG Staff',
        action: 'rename',
        entity_type: 'file',
        entity_id: item.id,
        entity_name: next,
        client_name: activeSession?.clientName || null,
        details: { from: item.file_name, to: next, storage_path: item.storage_path },
      }).then(() => {}, () => {});
      // ⛔ PATCH BOTH LISTS. displayName is a SNAPSHOT copied off file_name when the display row is built
      //    (:500) and again when a file is selected, so patching sessionFiles alone leaves an open
      //    selection — and the filing form's header — showing the old name. isSelected also compares
      //    displayName, so a half-patched selection would silently deselect the row.
      setSessionFiles(prev => prev.map(f => f.id === item.id ? { ...f, file_name: next } : f));
      setSelectedFiles(prev => prev.map(f => f.id === item.id ? { ...f, file_name: next, displayName: next } : f));
      setRenaming(null);
      setRenameMsg(null);
    } finally {
      setRenameSaving(false);
    }
  }

  // Every row is a real file_exchange row — split pages included (they have their own id, object and name).
  function buildDisplayList(files) {
    return files.map(f => ({ ...f, displayName: f.file_name }));
  }

  // Step-Gap1: Map a request item's document_type hint (may be plural or worded differently) to a type in the
  // folder table. Returns the type KEY, or null if no confident match. Only ACTIVE types are offered.
  function mapHintedDocType(hint) {
    if (!hint || !vocab) return null;
    // Only client and property types: a request item is for a client's property, never a consultant's own file
    // (else "Contract" could substring-match "Consultant Contract").
    const offered = (t) => t.active && vocab.folder(t.folder_key)?.level !== 'consultant';
    const exact = vocab.resolveType(hint);                // key, stored name or display name
    if (exact && offered(exact)) return exact.key;
    const norm = String(hint).toLowerCase().trim();
    const active = vocab.types.filter(offered);
    const lc = (t) => t.name.toLowerCase();
    // Strip a trailing 's' (plural) on either side
    const normSingular = norm.endsWith('s') ? norm.slice(0, -1) : norm;
    let m = active.find(t => lc(t).replace(/s$/, '') === normSingular);
    if (m) return m.key;
    // Substring match (for cases like "Income & Expense Statements" → "Income & Expense")
    m = active.find(t => norm.includes(lc(t)) || lc(t).includes(normSingular));
    return m ? m.key : null;
  }

  async function selectFile(item, multiSelect) {
    let next;
    if (multiSelect) {
      const already = selectedFiles.some(f => f.displayName === item.displayName && f.id === item.id);
      next = already ? selectedFiles.filter(f => !(f.displayName === item.displayName && f.id === item.id)) : [...selectedFiles, item];
    } else {
      next = [item];
    }
    const seq = ++selectSeq.current;
    setSelectedFiles(next);           // highlight immediately; the signed rows replace these below
    setScannerMode(false); setScanning(false);
    setIdentifyingId(null); setIdentifyFailedId(null);   // clear any prior file's identify status
    if (next.length > 0 && next[0].id) {
  claimFile(next[0].id, next[0].displayName);
    }

    // ⛔ The signed URL has to ride on the SELECTION, not only on sessionFiles: openScanner() reads
    //    sel.signedUrl synchronously off selectedFiles, and fileBase64()/runIdentify() read it too — none of
    //    them see a later sessionFiles patch.
    const signed = await Promise.all(next.map(async (sel) =>
      sel.signedUrl ? sel : { ...sel, signedUrl: await signedUrlFor(sel) }));
    if (seq !== selectSeq.current) return;   // a newer click landed while this one signed — let that one win
    next = signed;
    setSelectedFiles(next);
    setPreviewUrls(next.map((sel) => sel.signedUrl));

    // Prefill CLIENT only from a folder that actually HAS a client. A staff pile carries client_id: null and a PERSON
    // as its label ("Philip V") — never a valid client — so guard on clientId (the definitive invariant) as well as
    // the isStaff flag. For staff piles CLIENT is filled by pre-identify (below) or left empty for manual search; the
    // folder label must never reach the CLIENT field.
    if (activeSession && activeSession.clientId && !activeSession.isStaff && !form.clientId) {
      setForm(f => ({ ...f, clientId: activeSession.clientId, clientName: activeSession.clientName }));
      setClientSearch(activeSession.clientName || '');
    }

    // Step-Gap1: Auto-pre-fill the filing form from hints stored on the file
    // when the client uploaded it via the request-aware drop zones. Pre-fills:
    //   - docType   (from file_exchange.file_type set by ClientUpload's per-item drop)
    //   - property  (from file_exchange.property_id set on raw upload)
    //   - viewable_consultant / viewable_client (from request defaults — note that
    //     ClientUpload now sets viewable_consultant=false on raw, so we default
    //     it to TRUE here since admin's filing it for consultant visibility)
    //   - year (defaulted to current year already in form initial state)
    // Admin can override anything before clicking File.
    if (next.length === 1) {
      const single = next[0];
      const updates = {};
      if (single.file_type) {
        const mapped = mapHintedDocType(single.file_type);
        if (mapped) updates.docType = mapped;
      }
      if (single.property_id) {
        const matchedProp = (allProperties || []).find(p => p.id === single.property_id);
        if (matchedProp) updates.property = matchedProp;
      }
      // For files coming from a request flow (have an upload_token), default
      // visibility to consultant=true since the request was made BY the consultant.
      // Admin can uncheck if needed.
      if (single.upload_token) {
        updates.viewableConsultant = true;
        updates.viewableClient = true;  // client uploaded it — they can see their own work
      }
      if (Object.keys(updates).length > 0) {
        setForm(f => ({ ...f, ...updates }));
        if (updates.property) setPropSearch(updates.property.name || '');
      }
      // Pre-identify: cheap page-1 AI extract → shared cascade → prefill CLIENT/PROPERTY/PARCEL that aren't already
      // known from an authoritative source. PDF only; fire-and-forget with a stale guard (activeFileRef) so a slow
      // identify can never stomp the form for a different file. A folder-scoped client and a stored property/type
      // hint are authoritative and are NOT overridden; a lingering selection from the previous file is.
      const isPdf = /\.pdf$/i.test(single.file_name || '');
      const clientKnown = !!(activeSession && !activeSession.isStaff);   // folder piles carry the real client
      const propKnown = !!updates.property;                              // stored property hint on this file
      if (isPdf && single.id && !(clientKnown && propKnown)) runIdentify(single, single.id, clientKnown, propKnown);
    }
  }

  // Read a selected file's PDF bytes as base64, via its signed URL.
  async function fileBase64(sel) {
    if (sel.signedUrl) {
      const resp = await fetch(sel.signedUrl);
      const blob = await resp.blob();
      return await new Promise(res => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.readAsDataURL(blob); });
    }
    return null;
  }

  // Pre-identify one file: page-1 extract → shared cascade → prefill only the fields not already known. Stored hints
  // (property/type) and the folder client win; a stale identify for a since-clicked-away file is dropped.
  async function runIdentify(sel, fileId, clientKnown, propKnown) {
    setIdentifyingId(fileId);
    setIdentifyFailedId(prev => prev === fileId ? null : prev);
    let resolvedClient = clientKnown;   // folder piles already carry a client; staff piles start unresolved
    try {
      const base64 = await fileBase64(sel);
      if (!base64) return;
      // PAGE 1 ONLY — reuse /api/split-pdf (server-side pdf-lib, no AI). This is what makes identify cheap on long
      // docs (rent rolls / I&E run 20-50 pages); the AI then sees one page, not the whole file. Fall back to the
      // full doc if the split fails.
      let page1 = base64;
      try {
        const sres = await fetch('/api/split-pdf', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base64, fileName: sel.file_name || 'doc.pdf' }) });
        const sdata = await sres.json();
        if (sres.ok && sdata.pages?.[0]?.base64) page1 = sdata.pages[0].base64;
      } catch { /* split failed — fall back to the full doc */ }
      const res = await fetch('/api/extract', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ base64: page1, docType: 'identify' }) });
      const data = await res.json();
      if (!res.ok || data.error) return;
      const match = await cascadeMatch(data.result || {});
      // STALE GUARD — the user may have clicked another file while we waited; never prefill (or flag) a different file.
      if (activeFileRef.current !== fileId) return;
      const prop = (match && !match.unmatched) ? (match.property || null) : null;
      const parcels = (match && !match.unmatched) ? (match.parcels || []) : [];
      const upd = {};
      if (!clientKnown) {   // staff piles have no folder client — set it from the match (a candidates match still sets the client)
        if (match?.client) { upd.clientId = match.client.id; upd.clientName = match.client.name; }
        else if (prop?.client_id) {
          upd.clientId = prop.client_id;
          upd.clientName = prop.client_name || (allClients || []).find(c => String(c.id) === String(prop.client_id))?.name || '';
        }
      }
      resolvedClient = clientKnown || !!upd.clientId;
      if (!propKnown && prop) upd.property = prop;
      if (upd.property && parcels.length === 1) upd.parcelId = parcels[0].id;   // parcel only when it belongs to the property we just set
      if (Object.keys(upd).length === 0) return;
      setForm(prev => ({ ...prev, ...upd }));
      if (upd.clientName != null) setClientSearch(upd.clientName);
      if (upd.property) setPropSearch(upd.property.name || '');
    } catch (e) {
      console.error('[intake identify]', e);
    } finally {
      // Only touch status for the still-active file. No client resolved (and none from the folder) → surface a
      // "couldn't identify" note instead of leaving the CLIENT field silently blank.
      if (activeFileRef.current === fileId && !resolvedClient) setIdentifyFailedId(fileId);
      setIdentifyingId(cur => (cur === fileId ? null : cur));
    }
  }

  // Step-Gap1: After files are filed, check whether they were tied to a
  // document_request (via upload_token). If yes, see if all client-uploaded
  // files for that request are now intaked. If yes AND the related task is
  // currently 'Waiting on Intake', flip it to 'Ready to Deliver' and log a
  // system event to its email_thread.
  //
  // Called from fileDocument() AFTER the file_exchange updates have committed.
  async function reevaluateRequestsAfterIntake(filedFiles) {
    try {
      // Distinct upload_tokens from the filed batch (skip files with no token —
      // those weren't from a doc request)
      const tokens = [...new Set(filedFiles.map(f => f.upload_token).filter(Boolean))];
      if (tokens.length === 0) return;

      for (const token of tokens) {
        // Find the request linked to this token
        const { data: tokenRow } = await supabase.from('upload_tokens')
          .select('id').eq('token', token).maybeSingle();
        if (!tokenRow?.id) continue;

        const { data: req } = await supabase.from('document_requests')
          .select('id, property_name, status').eq('upload_token_id', tokenRow.id).maybeSingle();
        if (!req?.id) continue;

        // Are any files for this token still unintaked?
        const { data: allFiles } = await supabase.from('file_exchange')
          .select('id, need_review, filed_at, split_at')
          .eq('upload_token', token)
          .eq('direction', 'client_to_ntrg');
        // A split original is intaked through its pages — its own filed_at stays null by design.
        const stillUnintaked = (allFiles || []).filter(f => !f.split_at && (f.need_review === true || f.filed_at === null));
        if (stillUnintaked.length > 0) continue; // not yet — task stays Waiting on Intake

        // All files intaked. Find the related task and check its current status.
        const { data: relatedTask } = await supabase.from('tasks')
          .select('id, status, email_thread').eq('related_id', req.id).maybeSingle();
        if (!relatedTask?.id) continue;

        // Only auto-flip if currently in Waiting on Intake. Don't touch other states.
        if (relatedTask.status !== 'Waiting on Intake') continue;

        const transitionMsg = `All client-uploaded files have been processed through Document Intake by ${staffUser?.name || 'staff'}. Task is now ready to be delivered to the consultant.`;

        await supabase.from('tasks').update({
          status: 'Ready to Deliver',
          updated_at: new Date().toISOString(),
        }).eq('id', relatedTask.id);

        await supabase.from('task_activity').insert({
          task_id: relatedTask.id,
          staff_id: staffUser?.id,
          staff_name: staffUser?.name || 'System',
          action: 'intake_complete_ready_to_deliver',
          comment: transitionMsg,
        });

        const sysEntry = {
          id: Date.now().toString() + '-' + req.id.slice(0, 8),
          direction: 'system',
          from_name: 'System',
          body: transitionMsg,
          label: 'Ready to Deliver',
          sent_at: new Date().toISOString(),
        };
        const newThread = [...(relatedTask.email_thread || []), sysEntry];
        await supabase.from('tasks').update({ email_thread: newThread }).eq('id', relatedTask.id);
      }
    } catch (e) {
      console.warn('[reevaluateRequestsAfterIntake]', e);
    }
  }

  async function fileDocument() {
    if (!selectedFiles.length || !form.docType) return;
    // Resolve the type ONCE, before anything is written: an unknown type (or a table that hasn't loaded) stops here.
    const docTypeRow = vocab?.resolveType(form.docType);
    if (!docTypeRow) { alert(vocab ? `"${form.docType}" is not a document type in the folder table. Nothing was filed.` : 'Document folders are still loading — try again in a moment.'); return; }
    // A consultant-level type is filed against a consultant firm and needs no client (computed here, not from
    // isConsultantLevel further down — npm run lint:tdz).
    const toConsultant = vocab.folder(docTypeRow.folder_key)?.level === 'consultant';
    const consultant = toConsultant ? consultants.find(k => k.id === form.consultantId) : null;
    // Need to Review parks the file in the review pile (no consultant is stored there), so it needs neither.
    if (toConsultant && !form.needReview && !consultant) { alert('Please select a consultant.'); return; }
    if (toConsultant && !form.needReview && !form.year) { alert(`Please select the ${vocab.yearLabel(docTypeRow.folder_key).toLowerCase()}.`); return; }
    if (!toConsultant && !form.clientId) { alert('Please select a client.'); return; }
    setFiling(true);
    let filedFolder = null;   // set from buildFilingUpdates — same for every file in the batch (one docType)

    for (const sel of selectedFiles) {
      if (form.needReview) {
        const nr = vocab.folder('need_to_review');
        await supabase.from('file_exchange').update({ need_review: true, file_type: docTypeRow.legacy_name, type_key: docTypeRow.key, folder_name: nr.legacy_name, folder_key: nr.key, filed_by: staffUser?.name, filed_at: new Date().toISOString() }).eq('id', sel.id);
      } else {
        // buildFilingUpdates is THE filing model (docFiling.js) — shared with TaskEngine. It owns the
        // client-level rule, so a Contract can't inherit sel.property_id via existingPropertyId.
        const { updates, folder, docFields, clearParcelDocs } = buildFilingUpdates({
          vocab, docType: docTypeRow.key, clientId: form.clientId, consultantId: consultant?.id || null,
          // CLIENT_LEVEL is the explicit "no property" signal — it also defeats the existingPropertyId
          // fallback, so a client-level doc can't inherit the intake row's property.
          propertyId: isClientLevel ? CLIENT_LEVEL : (form.property?.id || null),
          parcelId: form.parcelId || null,
          year: form.year || null,
          viewableClient: form.viewableClient, viewableConsultant: form.viewableConsultant,
          existingPropertyId: sel.property_id || null, staffName: staffUser?.name,
        });
        filedFolder = folder;
        // ⛔ .select('id') so we get the AFFECTED ROWS, not just an error. file_exchange has RLS with write
        // policies scoped to UNFILED rows, so a blocked update returns 0 rows and NO error — checking only
        // { error } would still show the success toast over a write that did nothing. Both are checked; a
        // failure stops the batch rather than reporting "Filed" for work that didn't happen.
        const { data: updated, error: upErr } = await supabase.from('file_exchange')
          .update(updates).eq('id', sel.id).select('id');
        if (upErr || !updated?.length) {
          const done = selectedFiles.indexOf(sel);
          alert(
            `Filing stopped on "${sel.displayName || sel.file_name}".\n\n` +
            (upErr ? `Error: ${upErr.message}` : 'The write affected 0 rows — it was blocked, not saved.') +
            (done > 0 ? `\n\n${done} file(s) before it WERE filed.` : '') +
            `\n\nNothing else was filed. Try again, or send this message to Philip.`
          );
          setFiling(false);
          return;
        }
        if (updates.parcel_id) {   // the helper's answer, not the form's — null on the client-level path
          await supabase.from('parcel_documents').upsert({
            parcel_id: updates.parcel_id, file_exchange_id: sel.id, ...docFields,
            year_subfolder: form.year || null, property_id: form.property?.id || null,
          }, { onConflict: 'file_exchange_id' }).then(() => {}, () => {});
        } else if (clearParcelDocs) {
          // Re-filed as client-level: parcel_documents is a SECOND source of parcel visibility, so nulling
          // parcel_id alone would leave the doc showing on a parcel it no longer belongs to.
          const { error: pdDelErr } = await supabase.from('parcel_documents').delete().eq('file_exchange_id', sel.id);
          if (pdDelErr) console.warn('[fileDocument] parcel_documents cleanup:', pdDelErr.message);
        }
      }
    }

    // The folder's STORED name for the activity log (logs keep the stored vocabulary). filedFolder is null on the
    // need-review path (the file goes to 'Need to Review'), so fall back to the type's own folder.
    const filedFolderName = filedFolder || vocab.folder(docTypeRow.folder_key).legacy_name;

    // Log to activity_log
    await supabase.from('activity_log').insert({
      staff_user: staffUser?.name || 'NTRG Staff',
      action: 'file',
      entity_type: 'file',
      entity_name: selectedFiles.map(f => f.displayName).join(', '),
      client_name: toConsultant ? null : (form.clientName || null),
      property_name: toConsultant ? null : (form.property?.name || null),
      details: { doc_type: docTypeRow.legacy_name, folder: filedFolderName, count: selectedFiles.length,
        ...(consultant ? { consultant_id: consultant.id, consultant: consultant.company } : {}) },
    }).then(() => {}, () => {});

    await finishFiled([...selectedFiles], { docType: docTypeRow.name, count: selectedFiles.length,
      folder: consultant ? `${consultant.company} · ${vocab.folder(docTypeRow.folder_key).name}` : vocab.folder(docTypeRow.folder_key).name });
  }

  // After a batch has been filed — by the File button or by "File without scanning": release the files, show the
  // toast, reset the form, refresh the pile, and re-evaluate any document requests the batch touched.
  async function finishFiled(filedBatch, toast) {
    for (const sel of filedBatch) { if (sel.id) await releaseFile(sel.id); }
    setFiled(toast);
    setForm(f => ({ ...f, docType: '', needReview: false, viewableClient: false, viewableConsultant: false, parcelId: '' }));
    setSelectedFiles([]); setPreviewUrls([]);
    await loadSessions();
    await refreshOpenPile();
    // After reload, fire request re-evaluation. If any task should flip to
    // Ready to Deliver, it will happen now and be visible next time admin
    // returns to TaskEngine.
    await reevaluateRequestsAfterIntake(filedBatch);
    setFiling(false);
    setTimeout(() => setFiled(null), 4000);
  }

  // ── File a scan-required document WITHOUT scanning ─────────────────────────
  // The shared implementation (utils/fileWithoutScanning) — same rules as the scanner's: client, property, parcel,
  // type and year all set by hand (the year starts blank in this mode), nothing recorded but the document, and the
  // skip stored on the row. The Intake row itself is filed. One file at a time, like the scanner.
  // The type is resolved HERE, not via formType/isScannable (declared further down the component — npm run lint:tdz).
  const skipType = () => vocab?.type(form.docType) || null;
  const skipParcel = () => clientParcels.find(p => p.id === form.parcelId) || null;
  const currentSkipBlockers = () => {
    const out = [];
    if (selectedFiles.length !== 1) out.push('exactly one selected file');
    if (!form.clientId) out.push('a client');
    return out.concat(skipBlockers({ hasFile: selectedFiles.length === 1, vocab, typeKey: skipType()?.key, year: form.year, property: form.property, parcel: skipParcel() })
      .filter(b => b !== 'a file'));
  };
  function enterSkipMode() { setSkipMode({ prevYear: form.year }); setSkipReason(''); setSkipError(''); setForm(f => ({ ...f, year: '', needReview: false })); }
  function leaveSkipMode() { const prev = skipMode?.prevYear ?? ''; setSkipMode(null); setSkipReason(''); setSkipError(''); setForm(f => ({ ...f, year: prev })); }

  // Skip mode belongs to a scan-required type: changing to a file-only type (or clearing it) leaves it.
  useEffect(() => { const t = skipType(); if (skipMode && !(t && t.scan_as !== 'none')) leaveSkipMode(); }, [form.docType]);

  async function fileSkipped() {
    if (currentSkipBlockers().length || filing) return;
    const sel = selectedFiles[0];
    const type = skipType();
    setFiling(true); setSkipError('');
    const res = await fileWithoutScanning({
      vocab, typeKey: type.key, property: form.property, parcel: skipParcel(), year: form.year,
      staffName: staffUser?.name, reason: skipReason, existingId: sel.id,
      viewableClient: form.viewableClient, viewableConsultant: form.viewableConsultant, source: 'intake',
    }).catch((e) => ({ ok: false, error: `Filing failed — ${e.message || e}` }));
    if (!res.ok) { setSkipError(res.error); setFiling(false); return; }
    if (res.warning) alert(res.warning);
    const prevYear = skipMode?.prevYear ?? '';
    setSkipMode(null); setSkipReason('');
    setForm(f => ({ ...f, year: prevYear }));
    await finishFiled([sel], { docType: type.name, folder: `${res.folderName} (not scanned)`, count: 1 });
  }

  // ── Trigger inline scan ───────────────────────────────────────────────────
  function openScanner() {
    if (!selectedFiles.length) return;
    if (skipMode) leaveSkipMode();
    const sel = selectedFiles[0];
    setScannerFile({
      url: sel.signedUrl,
      name: sel.displayName,
      fileExchangeId: sel.id,
      // The bytes are ALREADY in R2 — hand the scanner the key so extraction can read them server-side
      // instead of posting them back up through a Vercel function (the ~4.5 MB body cap). A split page has its
      // own object, so this is always the selected page's own bytes.
      storagePath: sel.storage_path || null,
    });
    setScanning(true); // play animation first
    setScannerMode(false); // scanner shows after animation
  }

  // The scanner filed the tray's selected file — drop it from BOTH positional arrays. Left in, a filed
  // row reads as still-unfiled and the filing form would act on it again. ⛔ previewUrls[i] pairs with
  // selectedFiles[i] (see the preview map), so filtering one alone slides every remaining preview onto
  // the wrong file. Shared by BOTH scanner exits — Done and → Open Parcel — because they leave the
  // scanner the same way; the second one used to skip this and strand the filed file in the tray.
  // Re-read the OPEN pile after a file leaves it — filed from the form, or filed by the scanner.
  //
  // ⛔ ONE IMPLEMENTATION FOR BOTH PATHS. The File button rebuilt the pile inline; the scanner only called
  // loadSessions(), which refreshes the folder CARDS and never touches sessionFiles. So the same document,
  // filed two ways on the same screen, behaved two ways: through the form it vanished from the list,
  // through the scanner it sat there looking unfiled until someone reloaded. That asymmetry is why this
  // reads as a regression — the scan path never removed it, back to b8fd4b0 at least.
  //
  // The Need-to-Review pile is not token-backed: it is a QUERY, so it is re-run rather than looked up by
  // upload_token. The inline version did not know that, so filing one file out of Need to Review closed
  // the entire pile (no token → no rows → "empty").
  async function refreshOpenPile(session) {
    const sess = session || activeSession;
    if (!sess) return;
    const shape = (rows) => rows.map(f => ({ ...f, signedUrl: null }));
    if (sess.tokenId === 'review') {
      const { data } = await supabase.from('file_exchange').select('*').eq('need_review', true).eq('direction', 'client_to_ntrg').is('split_at', null);
      const rows = data || [];
      if (!rows.length) { setActiveSession(null); setSessionFiles([]); return; }
      setSessionFiles(shape(rows));
      return;
    }
    const unfiled = await loadPileFiles(sess.token);
    if (!unfiled || !unfiled.length) { setActiveSession(null); setSessionFiles([]); return; }
    setSessionFiles(shape(unfiled));
  }

  function dropFiledFromTray(fileExchangeId) {
    if (!fileExchangeId) return;
    const keep = selectedFiles.map(f => f.id !== fileExchangeId);
    setSelectedFiles(prev => prev.filter((_, i) => keep[i] !== false));
    setPreviewUrls(prev => prev.filter((_, i) => keep[i] !== false));
  }

  function onScanAnimationDone() {
    setScanning(false);
    setScannerMode(true); // now show the scanner inline
  }

  async function claimFile(fileId, fileName) {
    if (!staffUser?.name || !fileId) return;
    if (activeFileRef.current && activeFileRef.current !== fileId) {
      await supabase.from('intake_presence').delete().eq('file_id', activeFileRef.current).eq('staff_user', staffUser.name);
    }
    activeFileRef.current = fileId;
    await supabase.from('intake_presence').delete().eq('file_id', fileId).eq('staff_user', staffUser.name);
    await supabase.from('intake_presence').insert({ file_id: fileId, file_name: fileName, staff_user: staffUser.name, last_seen: new Date().toISOString(), token_id: activeSession?.token || null });
    await loadPresence();
  }

  async function releaseFile(fileId) {
    if (!staffUser?.name || !fileId) return;
    await supabase.from('intake_presence').delete().eq('file_id', fileId).eq('staff_user', staffUser.name);
    if (activeFileRef.current === fileId) activeFileRef.current = null;
    loadPresence();
  }

  async function loadPresence() {
    const { data } = await supabase.from('intake_presence').select('file_id, staff_user, last_seen').gte('last_seen', new Date(Date.now() - 15 * 60 * 1000).toISOString());
    const map = {};
    (data || []).forEach(p => { map[p.file_id] = p; });
    setFilePresence(map);
  }

  React.useEffect(() => {
    if (!activeSession) return;
    // Clear stale presence from previous sessions
    if (staffUser?.name) {
      supabase.from('intake_presence').delete().eq('staff_user', staffUser.name).then(() => loadPresence());
    } else {
      loadPresence();
    }
    heartbeatRef.current = setInterval(async () => {
      if (activeFileRef.current && staffUser?.name) {
        await supabase.from('intake_presence').upsert({ file_id: activeFileRef.current, staff_user: staffUser.name, last_seen: new Date().toISOString() }, { onConflict: 'file_id,staff_user' });
      }
      loadPresence();
    }, 30000);
    return () => {
      clearInterval(heartbeatRef.current);
      if (activeFileRef.current && staffUser?.name) {
        supabase.from('intake_presence').delete().eq('file_id', activeFileRef.current).eq('staff_user', staffUser.name);
      }
    };
  }, [activeSession?.token, staffUser?.name]);

  // ── Save one intake file to the local machine ─────────────────────────────────────────────────
  //
  // ⛔ fetch → blob → objectURL, NEVER `a.href = signedUrl; a.download = name`. The signed URL is
  // CROSS-ORIGIN (R2), and the browser IGNORES the download attribute cross-origin — the file opens
  // or navigates instead of saving, under a name derived from the storage key. The blob URL is
  // same-origin, so the attribute is honoured and the real file_name survives. Same pattern as
  // FileExchange.js's DownloadButton; see lib/r2.js presignDownload for the server-side alternative.
  //
  // ⛔ 'download' is the honest audit verb (file_access_log). A preview is not a download — the
  // distinction is the whole reason fileUrl keys its cache on action + path.
  //
  // ⛔ FAILURE IS LOUD. A save that did not happen must say so; a silently absent file is the
  // failure class this codebase keeps removing. Zero bytes counts as a failure, not a file.
  async function downloadIntakeFile(item, e) {
    e.stopPropagation();
    if (!item.storage_path || downloadingId === item.id) return;
    setDownloadingId(item.id);
    try {
      const url = await fileUrl(item.storage_path, 'download');
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (!blob.size) throw new Error('the stored file came back empty (0 bytes)');
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = item.file_name || item.displayName || 'download';
      a.click();
      setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
    } catch (err) {
      alert(`Could not download "${item.file_name || item.displayName}": ${err.message}`);
    }
    setDownloadingId(null);
  }

  // ⛔ DESTROY — for junk / duplicates / misdirected client uploads that shouldn't just be dismissed. Real
  // deletion via the file-delete endpoint (R2 object + row + parcel_documents, order-safe, audited). This is
  // the way to clear junk from the General Inbox without having to file it first.
  async function destroyFile(item, e) {
    e.stopPropagation();
    if (!window.confirm(`Permanently DELETE "${item.displayName || item.file_name}"?\n\nThis destroys the file (R2 object + record) and cannot be undone. Use ✓ to just dismiss from review and keep the file.`)) return;
    const { error } = await fileDelete(item.id);
    if (error) { alert('Could not delete: ' + error.message); return; }
    // Deleting the LAST page of a split brings its original back (server checks no page of it remains anywhere).
    if (item.split_from_id) await restoreIfEmpty(item);
    setSessionFiles(prev => prev.filter(f => f.id !== item.id));
    setSelectedFiles(prev => prev.filter(f => f.id !== item.id));
    setPreviewUrls([]);
    setSessions(prev => prev.map(s => s.tokenId === activeSession?.tokenId ? { ...s, fileCount: (s.fileCount || 1) - 1 } : s));
  }

  async function dismissFromReview(item, e) {
    e.stopPropagation();
    if (deleteConfirm !== item.id) { setDeleteConfirm(item.id); return; }
    // ⛔ DISMISS, not destroy — a triage-queue action must NEVER shred the document (§13.5). Clears
    // need_review so it leaves the Need-to-Review queue; the bytes + row are KEPT. Real deletion lives in
    // file management (/api/file-delete), never in triage.
    await supabase.from('file_exchange').update({ need_review: false }).eq('id', item.id).then(() => {}, () => {});
    await supabase.from('activity_log').insert({
      staff_user: staffUser?.name || 'NTRG Staff',
      action: 'dismiss',
      entity_type: 'file',
      entity_name: item.displayName || item.file_name,
      client_name: activeSession?.clientName || null,
      details: { file_name: item.file_name, storage_path: item.storage_path },
    }).then(() => {}, () => {});
    setDeleteConfirm(null);
    setSessionFiles(prev => prev.filter(f => f.id !== item.id));
    setSelectedFiles(prev => prev.filter(f => f.id !== item.id));
    setPreviewUrls([]);
    // Update session file count locally without full reload
    setSessions(prev => prev.map(s => s.tokenId === activeSession?.tokenId
      ? { ...s, fileCount: s.fileCount - 1 }
      : s
    ).filter(s => s.fileCount > 0));
  }

  const displayList = buildDisplayList(sessionFiles);
  const filteredClients = (allClients || []).filter(cl => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    if (cl.name.toLowerCase().includes(q)) return true;
    if (cl.legal_entity_names) {
      return cl.legal_entity_names.toLowerCase().includes(q);
    }
    return false;
  });
  const filteredProps = clientProps.filter(p =>
    !propSearch || p.name.toLowerCase().includes(propSearch.toLowerCase())
  );
  // The fork: a scannable type goes to Scan (which resolves the property); a non-scannable type shows the Property
  // field to file manually. Property/File appear only once a NON-scannable type is chosen.
  // From the folder table: a type with scan_as set must go through the scanner; its folder's level decides client-level.
  const formType = form.docType ? vocab?.type(form.docType) : null;
  const isScannable = !!formType && formType.scan_as !== 'none';
  // level:'client' types (Contract, Onboarding, Operations, Annual Review) file to the CLIENT — no property.
  const isClientLevel = !!formType && vocab.folder(formType.folder_key)?.level === 'client';
  // level:'consultant' types file to a consultant FIRM — no client, property or parcel; a year is required and its
  // label says what it means in that folder (doc_folders.year_label: effective year, tax year).
  const isConsultantLevel = !!formType && vocab.folder(formType.folder_key)?.level === 'consultant';
  const yearLabel = formType ? vocab.yearLabel(formType.folder_key) : 'Year';
  // What the File button still needs, in words (null = ready).
  const fileMissing = isConsultantLevel
    ? (form.needReview ? null : !form.consultantId ? 'a consultant' : !form.year ? `the ${yearLabel.toLowerCase()}` : null)
    : (!form.clientId ? 'a client' : (!isClientLevel && !form.property) ? 'a property' : null);

  // Full-bleed scanner — when scanning, render at the Intake PAGE level (horizontal/bottom bleed, mirroring Utilities'
  // margin:-24px -32px) instead of inside the narrow file-detail column. This restores the scanner's 58/42 split and
  // its calc(100vh…) preview heights as designed, so the PDF's native zoom toolbar is usable for verifying the
  // extraction. No scanner internals touched — same component, given the room it was built for.
  if (scannerFile && (scanning || scannerMode)) {
    return (
      <div style={s.page}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>Scan document</div>
          <button style={s.btnGhost} onClick={() => { setScannerMode(false); setScanning(false); setScannerFile(null); }}>← Back to files</button>
        </div>
        {scanning ? (
          <div style={{ minHeight: 'calc(100vh - 200px)' }}>
            <ScanAnimation previewUrl={previewUrls[0]} onDone={onScanAnimationDone} />
          </div>
        ) : (
          <div style={{ margin: '0 -32px -24px' }}>
            <ErrorBoundary isDark={darkMode} label="The scanner hit an error" resetLabel="← Back to files" onReset={() => { setScannerMode(false); setScannerFile(null); }}>
              <DocumentScanner
                darkMode={darkMode}
                staffUser={staffUser}
                allProperties={allProperties}
                allClients={allClients}
                intakeFile={scannerFile}
                intakeDocType={form.docType}
                onScanComplete={() => {
                  const sess = activeSession;
                  dropFiledFromTray(scannerFile?.fileExchangeId);
                  setScannerMode(false); setScannerFile(null);
                  loadSessions().then(() => refreshOpenPile(sess), () => {});
                }}
                onDrillParcel={(p) => {
                  const sess = activeSession;
                  dropFiledFromTray(scannerFile?.fileExchangeId);
                  setScannerMode(false); setScannerFile(null);
                  loadSessions().then(() => refreshOpenPile(sess), () => {});
                  if (onDrillParcel) onDrillParcel(p);
                }}
              />
            </ErrorBoundary>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '4px' }}>Document Intake</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '22px' }}>
        Files uploaded via secure links land here. Review, categorize, and file them to the correct property or parcel.
      </div>

      {/* Step-Gap1: Task-context banner.
          Shown when admin arrived here from a specific task's "Process in Document Intake" button.
          Filters the sessions list to that request's files and provides a Back-to-Task link. */}
      {taskContext?.token && (
        <div style={{ padding:'12px 16px', marginBottom:'22px', background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.35)', borderRadius:'10px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
          <div style={{ fontSize:'13px', color: isDark?'#fb923c':'#9a3412', lineHeight:'1.5' }}>
            <strong>🔗 Filtering to files from a specific document request.</strong>
            <div style={{ fontSize:'11px', marginTop:'3px', color: isDark?'#fdba74':'#7c2d12' }}>
              Only upload batches matching the request's token are shown below. Process all files, then return to the task to notify the consultant.
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button
              onClick={() => {
                try {
                  sessionStorage.removeItem('wh_intake_token');
                  sessionStorage.removeItem('wh_intake_return_task');
                } catch {}
                setTaskContext(null);
              }}
              style={{ padding:'7px 12px', background:'transparent', border:`1px solid ${c.inputBorder}`, borderRadius:'6px', color: c.sub, fontSize:'11px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
              Show all unfiled files
            </button>
            {taskContext.returnTask && (
              <button
                onClick={() => {
                  // Tab-switch back to TaskEngine and auto-open the originating task.
                  try {
                    sessionStorage.setItem('wh_intake_return_task', taskContext.returnTask);
                  } catch {}
                  window.location.href = `${window.location.pathname}?page=tasks`;
                }}
                style={{ padding:'7px 14px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'6px', color: 'white', fontSize:'11px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" }}>
                ← Back to Task
              </button>
            )}
          </div>
        </div>
      )}

      {/* Files a staff drop did NOT save, by name, until dismissed. */}
      {staffUploadMsg && (
        <div role="alert" style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(180,0,0,0.35)', background: isDark ? 'rgba(180,0,0,0.12)' : '#fff5f5', color: isDark ? '#f3c4c4' : '#7a0000', fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1, fontWeight: '600', lineHeight: '1.4' }}>{staffUploadMsg.text}</div>
            <button type="button" onClick={() => setStaffUploadMsg(null)} style={{ background: 'none', border: '1px solid currentColor', borderRadius: '6px', color: 'inherit', fontSize: '11px', padding: '2px 8px', cursor: 'pointer' }}>Dismiss</button>
          </div>
          <ul style={{ margin: '8px 0 0', paddingLeft: '18px', maxHeight: '180px', overflowY: 'auto', lineHeight: '1.5' }}>
            {staffUploadMsg.names.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}

      {/* Folder cards */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '22px', alignItems: 'flex-start' }}>
        {/* Staff upload — a persistent drop FIXTURE (first card). Drop/click → your pile (the "First L" folder). */}
        <div
          onClick={() => staffFileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); if (!dropHot) setDropHot(true); }}
          onDragLeave={e => { e.preventDefault(); setDropHot(false); }}
          onDrop={e => { e.preventDefault(); setDropHot(false); handleStaffUpload(e.dataTransfer.files); }}
          style={{ minWidth: '160px', maxWidth: '200px', minHeight: '132px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', textAlign: 'center', background: dropHot ? 'rgba(180,0,0,0.08)' : (isDark ? 'rgba(47,56,84,0.4)' : '#fafafa'), border: `2px dashed ${dropHot ? 'var(--red)' : (isDark ? '#434C68' : '#c8ccd8')}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: isDark ? '#a8b0cc' : '#4a5068' }}>{staffUploading ? 'Uploading…' : 'Drop files here'}</div>
          <div style={{ fontSize: '11px', color: !staffUploading && staffUploadMsg ? 'var(--red)' : 'var(--slate)', fontWeight: !staffUploading && staffUploadMsg ? '600' : '400', lineHeight: '1.3' }}>{staffUploading ? (staffQueued ? `${staffQueued} more queued` : '') : (staffUploadMsg ? 'Some files not saved — see the list' : 'or click to browse')}</div>
          <input ref={staffFileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { handleStaffUpload(e.target.files); e.target.value = ''; }} />
        </div>
        <FolderCard
          label="Need to Review" count={needToReview.length} icon="⚠️" color="248,113,113"
          active={activeSession?.tokenId === 'review'}
          onClick={() => {
            if (activeSession?.tokenId === 'review') { setActiveSession(null); setSessionFiles([]); setSelectedFiles([]); setPreviewUrls([]); return; }
            setActiveSession({ tokenId: 'review', clientName: 'Need to Review', files: needToReview });
            setSessionFiles(needToReview.map(f => ({ ...f, signedUrl: null })));
            setSelectedFiles([]); setPreviewUrls([]);
          }}
          isDark={isDark} c={c}
        />
        {(taskContext?.token
          ? sessions.filter(s => s.token === taskContext.token)
          : sessions
        ).map(sess => {
          const subParts = [];
          if (sess.sessionLabel) subParts.push(sess.sessionLabel);
          if (sess.requestCode) subParts.push(sess.requestCode);
          else if (sess.propertyName) subParts.push(sess.propertyName);
          const subLabel = subParts.length > 0 ? subParts.join(' · ') : null;
          return (
            <FolderCard key={sess.tokenId} label={sess.isStaff ? sess.clientName : sess.clientName === 'General Inbox' ? '📥 General Inbox' : sess.isConsultant ? `🏛 ${sess.clientName}` : sess.clientName} subLabel={subLabel}
              count={sess.fileCount} icon="📁" color="180,0,0"
              active={activeSession?.tokenId === sess.tokenId}
              onClick={() => openSession(sess)}
              uploadedAt={sess.uploadedAt} isDark={isDark} c={c}
            />
          );
        })}
        {sessions.length === 0 && needToReview.length === 0 && (
          <div style={{ fontSize: '13px', color: c.muted, padding: '16px 0', fontStyle: 'italic' }}>No files waiting — all caught up ✓</div>
        )}
        {taskContext?.token && sessions.filter(s => s.token === taskContext.token).length === 0 && (
          <div style={{ fontSize: '13px', color: '#22c55e', padding: '16px 0', fontWeight:'600', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <span>✓ All files from this request have been processed.</span>
            {taskContext.returnTask && (
              <>
                {autoReturnCountdown !== null && autoReturnCountdown > 0 && (
                  <span style={{ color: c.muted, fontWeight:'500' }}>Returning to task in {autoReturnCountdown}…</span>
                )}
                <button
                  onClick={() => {
                    try { sessionStorage.setItem('wh_intake_return_task', taskContext.returnTask); } catch {}
                    window.location.href = `${window.location.pathname}?page=tasks`;
                  }}
                  style={{ background:'transparent', border:'none', color: c.gold, textDecoration:'underline', cursor:'pointer', fontSize:'13px', fontWeight:'700' }}>
                  ← Back to Task now
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Work area */}
      {activeSession && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: '8px', alignItems: 'start' }}>

          {/* LEFT col */}
          <div>
            {/* Preview + scan animation */}
            <div style={{ ...s.card, minHeight: '480px', display: 'flex', flexDirection: 'column' }}>
              <div style={s.cardH}>
                <div style={s.cardT}>📄 Preview</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {selectedFiles.length > 0 && (
                    <span style={s.badge('180,0,0')}>{selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected</span>
                  )}
                  {/* Scan shows for a scannable type (or before a type is chosen) — never for a file-only type. The
                      scanner itself renders full-bleed at page level (early return), so no inline close button here. */}
                  {selectedFiles.length > 0 && (!form.docType || isScannable) && (
                    <button style={{ ...s.btn, background: 'linear-gradient(135deg,#B40000,#9C0000)', color: '#fff' }} onClick={openScanner}>
                      Scan
                    </button>
                  )}
                </div>
              </div>

              {/* Preview (the scanner + its animation render full-bleed at page level — see the early return above) */}
              <div style={{ flex: 1, display: 'flex', minHeight: '440px', flexDirection: 'column' }}>
                {(
                  // Normal preview
                  <div style={{ flex: 1, display: 'flex', gap: 0, minHeight: '440px' }}>
                    <div style={{ flex: 1, background: c.preview, display: 'flex', flexDirection: previewUrls.length > 1 ? 'row' : 'column', gap: '2px', overflow: 'hidden', borderRadius: '0 0 0 12px' }}>
                      {previewUrls.length === 0 ? (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: c.muted, gap: '10px' }}>
                          <div style={{ fontSize: '48px' }}>📄</div>
                          <div style={{ fontSize: '13px' }}>Click a file on the right to preview</div>
                          <div style={{ fontSize: '11px', color: c.muted }}>Shift-click to select multiple files</div>
                        </div>
                      ) : previewUrls.map((url, i) => {
                        const f = selectedFiles[i];
                        const name = f?.displayName || f?.file_name || '';
                        const isXlsx = /\.xlsx?$/i.test(name);
                        if (isXlsx) {
                          return <XlsxPreview key={i} url={url} fileName={name} isDark={isDark} />;
                        }
                        // ⛔ Guard falsy url — `${undefined}#...` is relative → SPA serves index.html → the app
                        // renders inside the preview (masked failure). Show an explicit error instead.
                        return url
                          ? <iframe key={i} src={`${url}#toolbar=1&navpanes=0&view=FitH`} style={{ flex: 1, border: 'none', minHeight: '440px', background: '#fff' }} title={`Preview ${i + 1}`} />
                          : <div key={i} style={{ flex: 1, minHeight: '440px', display:'flex', alignItems:'center', justifyContent:'center', background:'#fff', color:'#B40000', fontSize:'13px', textAlign:'center', padding:'24px' }}>Could not load this document — the file URL is empty (the file may not exist in storage).</div>;
                      })}
                    </div>

                    {/* Drag handle — only shown when filing form is visible */}
                    {selectedFiles.length > 0 && (
                      <div
                        onMouseDown={startDragFormPane}
                        title="Drag to resize"
                        style={{
                          width: '6px', flexShrink: 0,
                          cursor: 'col-resize',
                          background: c.border,
                          borderLeft: `1px solid ${c.border}`,
                          position: 'relative',
                        }}
                      >
                        {/* visual grip dots */}
                        <div style={{
                          position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                          display:'flex', flexDirection:'column', gap:'2px',
                        }}>
                          <span style={{ width:'2px', height:'2px', borderRadius:'50%', background:c.muted }} />
                          <span style={{ width:'2px', height:'2px', borderRadius:'50%', background:c.muted }} />
                          <span style={{ width:'2px', height:'2px', borderRadius:'50%', background:c.muted }} />
                        </div>
                      </div>
                    )}

                    {/* Filing form */}
                    {selectedFiles.length > 0 && (
                      <div style={{ width: `${formPaneWidth}px`, flexShrink: 0, borderLeft: `1px solid ${c.border}`, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px', background: c.card }}>

                        {filed && (
                          <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '8px', padding: '10px', fontSize: '12px', color: '#34d399' }}>
                            ✓ {filed.count} file{filed.count > 1 ? 's' : ''} filed → {filed.folder}
                          </div>
                        )}

                        {/* Client — not for a consultant-level type (filed against the firm, no client). */}
                        {!isConsultantLevel && <div>
                          <label style={s.label}>Client
                            {identifyingId && selectedFiles[0]?.id === identifyingId && !form.clientId && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '400', color: c.muted }}>Identifying…</span>
                            )}
                          </label>
                          <input style={s.input} placeholder="Type to search..." value={clientSearch}
                            onChange={e => { setClientSearch(e.target.value); if (!e.target.value) setForm(f => ({ ...f, clientId: '', clientName: '' })); }} />
                          {identifyFailedId && selectedFiles[0]?.id === identifyFailedId && !form.clientId && (
                            <div style={{ fontSize: '11px', color: c.gold, marginTop: '4px' }}>Couldn't auto-identify the client — search above.</div>
                          )}
                          {clientSearch && filteredClients.length > 0 && filteredClients[0]?.name !== clientSearch && (
                            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '7px', marginTop: '4px', maxHeight: '140px', overflowY: 'auto', zIndex: 10, position: 'relative' }}>
                              {filteredClients.slice(0, 8).map(cl => (
                                <div key={cl.id} onClick={() => { setForm(f => ({ ...f, clientId: cl.id, clientName: cl.name })); setClientSearch(cl.name); }}
                                  style={{ padding: '8px 12px', fontSize: '12px', cursor: 'pointer', color: c.text, borderBottom: `1px solid ${c.border}` }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(180,0,0,0.08)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {cl.name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>}

                        {/* Document type */}
                        <div>
                          <label style={s.label}>Document Type</label>
                          <select style={s.select} value={form.docType} onChange={e => {
                            const dt = e.target.value;
                            // Switching TO a client-level type drops any property/parcel already picked — the
                            // doc has no property, and a stale one would otherwise sit in state unseen.
                            const t = vocab?.type(dt);
                            const toLevel = t ? vocab.folder(t.folder_key)?.level : null;
                            const noProperty = toLevel === 'client' || toLevel === 'consultant';
                            if (noProperty) setPropSearch('');
                            // A consultant-level type is staff-only (filing forces both flags off — clear them on screen too).
                            setForm(f => ({ ...f, docType: dt, ...(noProperty ? { property: null, parcelId: '' } : {}),
                              ...(toLevel === 'consultant' ? { viewableClient: false, viewableConsultant: false } : {}) }));
                          }}>
                            <option value="">— Select type —</option>
                            <DocTypeOptions vocab={vocab} current={form.docType} levels={DOC_LEVELS} />
                          </select>
                          {formType && (
                            <div style={{ fontSize: '11px', color: c.gold, marginTop: '4px', fontFamily: 'monospace' }}>
                              → {isConsultantLevel ? 'Consultant · ' : ''}{vocab.folder(formType.folder_key)?.name}
                            </div>
                          )}
                          {isScannable && !skipMode && (
                            <div style={{ marginTop: '6px', padding: '6px 10px', background: 'rgba(180,0,0,0.08)', border: '1px solid rgba(180,0,0,0.2)', borderRadius: '6px', fontSize: '11px', color: '#B40000' }}>
                              Hit Scan above — the scan resolves the property for you.
                              <div style={{ marginTop: '6px', color: c.muted }}>
                                Must not be recorded (e.g. a superseded bill), or can't be scanned?{' '}
                                <button type="button" onClick={enterSkipMode}
                                  style={{ background: 'none', border: 'none', padding: 0, color: '#B40000', textDecoration: 'underline', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
                                  File without scanning
                                </button>
                              </div>
                            </div>
                          )}
                          {isScannable && skipMode && (
                            <div style={{ marginTop: '6px', padding: '6px 10px', border: `1px dashed ${c.border}`, borderRadius: '6px', fontSize: '11px', color: c.muted }}>
                              Filing without scanning: pick the property, parcel and year by hand. Nothing is recorded except the document.
                            </div>
                          )}
                        </div>

                        {/* Client-level type → no property at all (the helper forces property_id null). */}
                        {isClientLevel && form.clientId && (
                          <div style={{ fontSize: '11px', color: c.muted, fontStyle: 'italic', padding: '6px 10px', border: `1px dashed ${c.border}`, borderRadius: '7px' }}>
                            Client-level document → files to {form.clientName} · no property
                          </div>
                        )}

                        {/* Consultant — a consultant-level type files against the firm. Staff-only: no visibility checkboxes. */}
                        {isConsultantLevel && (
                          <div>
                            <label style={s.label}>Consultant</label>
                            <select style={s.select} value={form.consultantId} onChange={e => setForm(f => ({ ...f, consultantId: e.target.value }))}>
                              <option value="">— Select consultant —</option>
                              {consultants.map(k => <option key={k.id} value={k.id}>{k.company}</option>)}
                            </select>
                            {consultantsErr && <div style={{ fontSize: '11px', color: '#f87171', marginTop: '4px' }}>Consultants could not be loaded: {consultantsErr}</div>}
                            <div style={{ fontSize: '11px', color: c.muted, fontStyle: 'italic', marginTop: '4px' }}>Consultant document → staff only · no client or property</div>
                          </div>
                        )}

                        {/* Property — for a NON-scannable, non-client-level type (a scannable type resolves the property via Scan),
                            and for a scannable one being filed without scanning. */}
                        {form.clientId && form.docType && (!isScannable || skipMode) && !isClientLevel && !isConsultantLevel && (
                          <div>
                            <label style={s.label}>Property</label>
                            {form.clientName && (
                              <div style={{ fontSize: '11px', color: c.muted, margin: '0 0 5px' }}>Showing {form.clientName}'s properties</div>
                            )}
                            <input style={{ ...s.input, marginBottom: '6px' }} placeholder="Search property..." value={propSearch}
                              onChange={e => setPropSearch(e.target.value)} />
                            <div style={{ maxHeight: '120px', overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: '7px' }}>
                              {filteredProps.slice(0, 20).map(p => (
                                <div key={p.id} onClick={() => { setForm(f => ({ ...f, property: p })); setPropSearch(p.name); }}
                                  style={{ padding: '7px 10px', fontSize: '12px', cursor: 'pointer', color: c.text, background: form.property?.id === p.id ? 'rgba(180,0,0,0.1)' : 'transparent', borderBottom: `1px solid ${c.border}` }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(180,0,0,0.06)'}
                                  onMouseLeave={e => e.currentTarget.style.background = form.property?.id === p.id ? 'rgba(180,0,0,0.1)' : 'transparent'}>
                                  <div style={{ fontWeight: '600' }}>{p.name}</div>
                                  {p.city && <div style={{ fontSize: '11px', color: c.muted }}>{p.city}, {p.state}</div>}
                                </div>
                              ))}
                              {filteredProps.length === 0 && <div style={{ padding: '8px 10px', fontSize: '12px', color: c.muted }}>{propSearch ? 'No match' : 'No properties'}</div>}
                            </div>
                          </div>
                        )}

                        {/* Year */}
                        <div>
                          <label style={s.label}>{yearLabel}{skipMode ? ' (required — pick by hand)' : isConsultantLevel ? ' (required)' : ''}</label>
                          <select style={s.select} value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                            <option value="">{skipMode || isConsultantLevel ? `— Select ${yearLabel.toLowerCase()} —` : '— No year —'}</option>
                            {YEARS.map(y => <option key={y}>{y}</option>)}
                          </select>
                        </div>

                        {/* Parcel */}
                        {form.property && clientParcels.length > 0 && (
                          <div>
                            <label style={s.label}>Parcel / Assessor ID {skipMode ? '(required)' : '(optional)'}</label>
                            <select style={s.select} value={form.parcelId} onChange={e => setForm(f => ({ ...f, parcelId: e.target.value }))}>
                              <option value="">{skipMode ? '— Select parcel —' : '— Property level only —'}</option>
                              {clientParcels.map(p => (
                                <option key={p.id} value={p.id}>{p.account_number || p.parcel_name} — {p.re_pp}</option>
                              ))}
                            </select>
                            {form.parcelId && <div style={{ fontSize: '11px', color: '#34d399', marginTop: '4px' }}>✓ Will file at parcel AND property level</div>}
                          </div>
                        )}

                        {/* Checkboxes — a consultant-level document is staff-only, so only Need to Review is offered. */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {[...(isConsultantLevel ? [] : [['viewableClient','👤 Viewable by Client'],['viewableConsultant','🏛 Viewable by Consultant']]),...(skipMode ? [] : [['needReview','⚠️ Need to Review']])].map(([key, label]) => (
                            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: c.text }}>
                              <input type="checkbox" checked={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} style={{ accentColor: 'var(--red)', width: '14px', height: '14px' }} />
                              {label}
                            </label>
                          ))}
                        </div>

                        {/* File without scanning — a scannable type in skip mode. Names what is missing instead of sitting disabled. */}
                        {form.docType && isScannable && skipMode && (() => {
                          const blockers = currentSkipBlockers();
                          return (
                            <>
                              <div>
                                <label style={s.label}>Reason (optional)</label>
                                <input style={s.input} value={skipReason} maxLength={300} placeholder="e.g. superseded by corrected bill"
                                  onChange={e => setSkipReason(e.target.value)} />
                              </div>
                              {blockers.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#f87171', fontWeight: 600 }}>⚠ Set {blockers.join(', ')} before filing. Nothing is inferred on this path.</div>
                              )}
                              {skipError && <div style={{ fontSize: '11px', color: '#f87171', fontWeight: 600 }}>{skipError}</div>}
                              <button onClick={fileSkipped} disabled={filing || blockers.length > 0}
                                style={{ ...s.btn, width: '100%', opacity: (filing || blockers.length) ? 0.5 : 1, marginTop: '4px' }}>
                                {filing ? '⏳ Filing...' : '✓ File without scanning'}
                              </button>
                              <button type="button" onClick={leaveSkipMode} style={{ ...s.btnGhost, width: '100%' }}>Cancel — scan it instead</button>
                            </>
                          );
                        })()}

                        {/* File button — only for a NON-scannable type; scannable types file through Scan. */}
                        {form.docType && !isScannable && (
                          <>
                            <button onClick={fileDocument}
                              disabled={filing || !!fileMissing}
                              style={{ ...s.btn, width: '100%', opacity: fileMissing ? 0.5 : 1, marginTop: '4px' }}>
                              {filing ? '⏳ Filing...' : form.needReview ? '⚠️ Move to Review' : `✓ File Document${selectedFiles.length > 1 ? 's' : ''}`}
                            </button>
                            {fileMissing && (
                              <div style={{ fontSize: '11px', color: c.muted, textAlign: 'center' }}>
                                Select {fileMissing} to file
                              </div>
                            )}
                          </>
                        )}
                        {!form.docType && (
                          <div style={{ fontSize: '11px', color: c.muted, textAlign: 'center' }}>Select a document type to continue</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: File list */}
          <div style={s.card}>
            <div style={s.cardH}>
              <div>
                <div style={s.cardT}>{activeSession.tokenId === 'review' ? '⚠️ Need to Review' : `📁 ${activeSession.clientName}`}</div>
                {activeSession.requestCode && (
                  <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '11px', fontWeight: '700', color: '#956f1d', marginTop: '2px' }}>
                    {activeSession.requestCode}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <span style={s.badge('180,0,0')}>{displayList.length}</span>
                <button onClick={() => { setActiveSession(null); setSessionFiles([]); setSelectedFiles([]); setPreviewUrls([]); setScannerMode(false); }} style={s.btnGhost}>✕</button>
              </div>
            </div>
            {splitting && <div style={{ padding: '12px 16px', fontSize: '12px', color: c.muted }}>⏳ Loading files...</div>}
            {activeSession?.comment && (
              <div style={{ margin: '10px 14px 0', padding: '10px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', fontSize: '12px', color: isDark ? '#e8eaf2' : 'var(--ink)' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>💬 Uploader Note</div>
                {activeSession.comment}
              </div>
            )}
            <div style={{ maxHeight: 'calc(100vh - 340px)', overflowY: 'auto' }}>
              {displayList.map((item, idx) => {
                const isSelected = selectedFiles.some(f => f.displayName === item.displayName && f.id === item.id);
                const isPdf = item.file_name?.toLowerCase().endsWith('.pdf');
                const isSplit = !!item.split_from_id;
                const isXlsxFile = /\.xlsx?$/i.test(item.file_name || '');
                const hasMultiPage = (isPdf || isXlsxFile) && !isSplit;
                const isRenaming = !!(renaming && renaming.id === item.id);
                return (
                  <div key={`${item.id}-${idx}`}
                    style={{ padding: '10px 14px', borderBottom: `1px solid ${c.border}`, background: isSelected ? (isDark ? 'rgba(180,0,0,0.1)' : 'rgba(180,0,0,0.07)') : 'transparent', cursor: 'pointer', borderLeft: isSelected ? '3px solid #B40000' : '3px solid transparent', transition: 'all 0.12s' }}
                    onClick={(e) => selectFile(item, e.shiftKey || e.metaKey)}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(180,0,0,0.04)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px', flexShrink: 0 }}>{isSplit ? '📄' : isPdf ? '📋' : item.file_name?.match(/\.(xlsx?|csv)$/i) ? '📊' : '📎'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isRenaming ? (
                          // stopPropagation on the WRAPPER: the row's own onClick selects the file, and a
                          // click into the text box or its buttons must not do that.
                          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input autoFocus value={renaming.stem}
                              onChange={(e) => setRenaming({ id: item.id, stem: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveRename(item);
                                if (e.key === 'Escape') { setRenaming(null); setRenameMsg(null); }
                              }}
                              style={{ ...s.input, flex: 1, minWidth: 0, fontSize: '12px', padding: '3px 6px' }} />
                            {/* The extension is shown, not editable — see saveRename. */}
                            <span style={{ fontSize: '11px', color: c.muted, flexShrink: 0 }}>{splitName(item.file_name)[1]}</span>
                            <button onClick={() => saveRename(item)} disabled={renameSaving} title="Save the new name"
                              style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', borderRadius: '5px', cursor: renameSaving ? 'wait' : 'pointer', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.12)', color: '#34d399' }}>✓</button>
                            <button onClick={() => { setRenaming(null); setRenameMsg(null); }} title="Cancel"
                              style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', borderRadius: '5px', cursor: 'pointer', border: '1px solid transparent', background: 'transparent', color: 'var(--slate)' }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', fontWeight: '600', color: isSelected ? 'var(--red)' : c.text, wordBreak: 'break-word', lineHeight: '1.3' }}>{item.displayName}</div>
                        )}
                        {renameMsg && renameMsg.id === item.id && (
                          <div style={{ marginTop: '3px', fontSize: '11px', lineHeight: '1.35', color: 'var(--red)' }}>{renameMsg.text}</div>
                        )}
                        {item.file_size_bytes && <div style={{ fontSize: '11px', color: c.muted, marginTop: '1px' }}>{(item.file_size_bytes / 1024).toFixed(0)} KB</div>}
                        {filePresence[item.id] && filePresence[item.id].staff_user !== staffUser?.name && (
                          <div style={{ display:'inline-flex', alignItems:'center', gap:'4px', marginTop:'3px', padding:'2px 7px', borderRadius:'10px', background:'rgba(251,191,36,0.12)', border:'1px solid rgba(251,191,36,0.3)' }}>
                            <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#fbbf24' }} />
                            <span style={{ fontSize:'10px', color:'#fbbf24', fontWeight:'600' }}>{filePresence[item.id].staff_user}</span>
                          </div>
                        )}
                        {filePresence[item.id] && filePresence[item.id].staff_user === staffUser?.name && (
                          <div style={{ display:'inline-flex', alignItems:'center', gap:'4px', marginTop:'3px', padding:'2px 7px', borderRadius:'10px', background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)' }}>
                            <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:'#60a5fa' }} />
                            <span style={{ fontSize:'10px', color:'#60a5fa', fontWeight:'600' }}>You</span>
                            <button onClick={(e) => { e.stopPropagation(); releaseFile(item.id); }} style={{ background:'none', border:'none', color:'#60a5fa', cursor:'pointer', fontSize:'10px', padding:'0 2px' }}>✕</button>
                          </div>
                        )}
                      </div>
                      {/* Every button here acts on THIS row's own id and storage_path. A split page is a row of its
                          own (api/split-pdf), so none of them can reach the original it came from. */}
                      {!isRenaming && (<>
                      <button onClick={(e) => { e.stopPropagation(); setRenameMsg(null); setRenaming({ id: item.id, stem: splitName(item.file_name)[0] }); }}
                        title="Rename this file"
                        style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', borderRadius: '5px', cursor: 'pointer', border: '1px solid transparent', background: 'transparent', color: 'var(--slate)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--slate)'; e.currentTarget.style.borderColor = 'transparent'; }}>✎</button>
                      {item.storage_path && (
                        <button onClick={(e) => downloadIntakeFile(item, e)} title="Download this file"
                          disabled={downloadingId === item.id}
                          style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', borderRadius: '5px', cursor: downloadingId === item.id ? 'wait' : 'pointer', border: '1px solid transparent', background: 'transparent', color: 'var(--slate)', transition: 'all 0.15s' }}
                          onMouseEnter={e => { if (downloadingId !== item.id) { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.3)'; } }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--slate)'; e.currentTarget.style.borderColor = 'transparent'; }}>
                          {downloadingId === item.id ? '⏳' : '⬇'}
                        </button>
                      )}
                      <button
                        onClick={(e) => dismissFromReview(item, e)}
                        style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', fontWeight: '600', borderRadius: '5px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", border: deleteConfirm === item.id ? '1px solid rgba(248,113,113,0.6)' : '1px solid transparent', background: deleteConfirm === item.id ? 'rgba(248,113,113,0.15)' : 'transparent', color: deleteConfirm === item.id ? '#f87171' : 'var(--slate)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { if (deleteConfirm !== item.id) { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.3)'; } }}
                        onMouseLeave={e => { if (deleteConfirm !== item.id) { e.currentTarget.style.color = 'var(--slate)'; e.currentTarget.style.borderColor = 'transparent'; } }}>
                        {deleteConfirm === item.id ? 'Dismiss?' : '✓'}
                      </button>
                      <button onClick={(e) => destroyFile(item, e)} title="Delete permanently (destroys the file)"
                        style={{ flexShrink: 0, padding: '3px 7px', fontSize: '11px', borderRadius: '5px', cursor: 'pointer', border: '1px solid transparent', background: 'transparent', color: 'var(--slate)', marginLeft: '2px' }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.borderColor = 'rgba(248,113,113,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--slate)'; e.currentTarget.style.borderColor = 'transparent'; }}>🗑</button>
                      </>)}
                    </div>
                    {hasMultiPage && (
                      <button onClick={(e) => { e.stopPropagation(); splitFile(item); }} disabled={splitting}
                        style={{ ...s.btnSm, marginTop: '6px', fontSize: '11px', background: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)' }}>
                        ⚙ {isXlsxFile ? 'Split sheets' : 'Split pages'}
                      </button>
                    )}
                    {isSplit && (
                      <button onClick={(e) => undoSplit(item, e)} disabled={splitting} title="Remove all pages split from the original and restore it"
                        style={{ ...s.btnSm, marginTop: '6px', fontSize: '11px', background: 'transparent', color: c.muted, border: `1px solid ${c.border}` }}>
                        ↶ Undo split
                      </button>
                    )}
                    {splitMsg && splitMsg.id === item.id && (
                      <div style={{ marginTop: '5px', fontSize: '11px', lineHeight: '1.35', color: splitMsg.tone === 'error' ? 'var(--red)' : c.muted }}>
                        {splitMsg.text}
                      </div>
                    )}
                  </div>
                );
              })}
              {displayList.length === 0 && !splitting && (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: c.muted }}>All files in this folder have been filed ✓</div>
              )}
            </div>
            <div style={{ padding: '10px 14px', borderTop: `1px solid ${c.border}`, fontSize: '11px', color: c.muted }}>Click to select · Shift+click for multiple</div>
          </div>
        </div>
      )}

      {/* Folder reference */}
      <div style={{ marginTop: '24px', ...s.card }}>
        <div style={s.cardH}>
          <div style={s.cardT}>📂 Folder Reference</div>
          <div style={{ fontSize: '11px', color: c.muted }}>Hover a folder to see file types</div>
        </div>
        <div style={{ padding: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Property Folders</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {/* From the folder table — the same source as the Document Type list, so the two always agree. */}
              {(vocab?.foldersByLevel('property') || []).map(f => (
                <FolderPill key={f.key} name={f.name} tip={vocab.typesIn(f.key).map(t => t.name).join(' / ')} isDark={isDark} c={c} />
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#303030', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Client Folders</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(vocab?.foldersByLevel('client') || []).map(f => (
                <FolderPill key={f.key} name={f.name} tip={vocab.typesIn(f.key).map(t => t.name).join(' / ')} isDark={isDark} c={c} color="48,48,48" />
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#0e7490', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Consultant Folders</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(vocab?.foldersByLevel('consultant') || []).map(f => (
                <FolderPill key={f.key} name={f.name} tip={vocab.typesIn(f.key).map(t => t.name).join(' / ')} isDark={isDark} c={c} color="14,116,144" />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

function FolderCard({ label, subLabel, count, icon, color, active, onClick, uploadedAt, isDark, c }) {
  return (
    <div onClick={onClick} style={{ minWidth: '160px', maxWidth: '200px', background: active ? `rgba(${color},0.12)` : (isDark ? 'rgba(47,56,84,0.6)' : '#fff'), border: `2px solid ${active ? `rgb(${color})` : (isDark ? '#374060' : 'var(--line)')}`, borderRadius: '12px', padding: '16px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: active ? `0 0 0 3px rgba(${color},0.15)` : 'none' }}
      onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = `rgb(${color})`; e.currentTarget.style.background = `rgba(${color},0.06)`; } }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = isDark ? '#374060' : 'var(--line)'; e.currentTarget.style.background = isDark ? 'rgba(47,56,84,0.6)' : '#fff'; } }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '13px', fontWeight: '700', color: `rgb(${color})`, marginBottom: '2px', lineHeight: '1.3' }}>{label}</div>
      {subLabel && <div style={{ fontSize: '11px', color: 'var(--slate)', marginBottom: '4px' }}>{subLabel}</div>}
      <div style={{ fontSize: '22px', fontWeight: '700', color: `rgb(${color})`, fontFamily: "'Archivo',sans-serif" }}>{count}</div>
      <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{count === 1 ? 'file' : 'files'}{uploadedAt ? ` · ${new Date(uploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</div>
    </div>
  );
}

function FolderPill({ name, tip, isDark, c, color = '180,0,0' }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'default', background: `rgba(${color},0.1)`, color: `rgb(${color})`, border: `1px solid rgba(${color},0.25)` }}>{name}</div>
      {hovered && tip && (
        <div style={{ position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '6px', padding: '7px 11px', background: isDark ? 'var(--ink)' : '#ffffff', border: `1px solid rgba(${color},0.3)`, borderRadius: '8px', fontSize: '11px', color: isDark ? '#a8b0cc' : '#4a5068', whiteSpace: 'nowrap', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 100, lineHeight: '1.5' }}>{tip}</div>
      )}
    </div>
  );
}
